
import { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ScrollToBottomButtonProps {
  isDarkMode: boolean;
  bottomOffsetPx?: number;
  className?: string;
  staticPosition?: boolean;
}


export const ScrollToBottomButton = ({ isDarkMode, bottomOffsetPx = 128, className, staticPosition = false }: ScrollToBottomButtonProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [idleHidden, setIdleHidden] = useState(false);
  const idleTimerRef = useRef<number | null>(null);
  const lastYRef = useRef<number>(0);
  const [mode, setMode] = useState<'toBottom' | 'toTop'>('toBottom');
  const modeRef = useRef<'toBottom' | 'toTop'>('toBottom');
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    const checkScrollPosition = () => {
      // Always track the main window scroll, so behavior is consistent on mobile
      // where the whole page scrolls instead of an inner container.
      const scrollPosition = window.pageYOffset;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      const bottomThreshold = 120;
      const isNearBottom = scrollPosition + windowHeight >= documentHeight - bottomThreshold;

      // Detect scroll direction to flip icon between up/down
      const lastY = lastYRef.current;
      const delta = scrollPosition - lastY;
      lastYRef.current = scrollPosition;

      if (Math.abs(delta) > 2) {
        if (delta > 0) setMode('toBottom');
        else setMode('toTop');
      }

      // Show the button when not near the bottom and user has scrolled a bit.
      const shouldShow = !isNearBottom && scrollPosition > 10;
      setIsVisible(shouldShow);

      // Any scroll that should show the button resets idle hide and restarts timer.
      if (shouldShow) {
        setIdleHidden(false);
        if (idleTimerRef.current) {
          window.clearTimeout(idleTimerRef.current);
        }
        idleTimerRef.current = window.setTimeout(() => {
          setIdleHidden(true);
        }, 3000);
      } else {
        // If not visible by position, clear timer and keep it hidden.
        if (idleTimerRef.current) {
          window.clearTimeout(idleTimerRef.current);
          idleTimerRef.current = null;
        }
        setIdleHidden(true);
      }
    };

    window.addEventListener('scroll', checkScrollPosition, { passive: true });
    checkScrollPosition();

    return () => window.removeEventListener('scroll', checkScrollPosition);
  }, []);

  const scrollToExtreme = useCallback((dir: 'top' | 'bottom') => {
    const doScroll = () => {
      const top = dir === 'top' ? 0 : document.documentElement.scrollHeight;
      window.scrollTo({ top, behavior: 'smooth' });
    };

    doScroll();
    window.setTimeout(doScroll, 120);
    window.setTimeout(doScroll, 320);
  }, []);

  const onClick = () => {
    if (mode === 'toBottom') {
      scrollToExtreme('bottom');
    } else {
      scrollToExtreme('top');
    }
  };

  if (!isVisible && !staticPosition) return null; // If static, we might render invisible to occupy space? Probs not.
  if (!isVisible) return null;

  const baseClasses = `z-30 rounded-full w-8 h-8 p-0 overflow-hidden backdrop-blur-sm backdrop-filter shadow-md transition-all duration-200 ease-out border ${isDarkMode
      ? `bg-[#303030]/65 hover:bg-[#3a3a3a]/80 border-white/10 text-white`
      : `bg-white/65 hover:bg-white/80 border-black/5 text-gray-700`
    }
  ${className || ''}
`;

  const fixedClasses = `fixed left-1/2 transform -translate-x-1/2`;

  const style: React.CSSProperties = staticPosition ? {
    opacity: idleHidden ? 0 : 1,
    pointerEvents: idleHidden ? 'none' : 'auto',
    marginBottom: '8px', // spacing
    transition: 'opacity 220ms ease, margin 220ms ease',
  } : {
    marginTop: '-7px',
    bottom: `${Math.max(16, bottomOffsetPx)}px`,
    opacity: idleHidden ? 0 : 1,
    pointerEvents: idleHidden ? 'none' : 'auto',
    transform: `translateX(-50%) translateY(${idleHidden ? 6 : 0}px)`,
    transition: 'bottom 220ms ease, transform 220ms ease, opacity 220ms ease',
    willChange: 'transform',
  };

  return (
    <Button
      onClick={onClick}
      className={`${baseClasses} ${staticPosition ? '' : fixedClasses}`}
      style={style}
    >
      <ArrowDown
  className="w-5 h-5 transition-all duration-200 ease-out"
        style={{
          transform: mode === 'toTop'
            ? 'rotate(180deg)'  
            : 'rotate(0deg)',
        }}
      />
    </Button>
  );
};

export default ScrollToBottomButton;
